import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { AtualizarDestinatarioRequest, NFeTipoDestinatario } from '../../../../types/nfe';
import 'components/common/CommandPalette.css';

// ---- API types ----

interface DestinatarioItem {
  id: string;
  nome: string;
  documento?: string;
  tipoPessoa: 'J' | 'F';
  inscricaoEstadual?: string;
  email?: string;
  telefone?: string;
  endereco?: {
    cep?: string;
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    municipio?: string;
    uf?: string;
    municipioId?: number;
  };
}

type Fonte = 'pessoa' | 'empresa';

// ---- Debounce hook ----

function useDebounce(value: string, delay = 400): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---- API ----

const buscarDestinatarios = async (termo: string, fonte: Fonte): Promise<DestinatarioItem[]> => {
  const { data } = await axios.get('/api/nfe/buscar-destinatario', {
    params: { termo: termo || undefined, fonte },
  });
  return data.itens ?? [];
};

// ---- Conversion ----

const itemToRequest = (item: DestinatarioItem): AtualizarDestinatarioRequest => {
  const isPJ = item.tipoPessoa === 'J';
  return {
    tipo: isPJ ? NFeTipoDestinatario.Juridica : NFeTipoDestinatario.Fisica,
    cnpj: isPJ ? item.documento : undefined,
    cpf: !isPJ ? item.documento : undefined,
    nomeRazaoSocial: item.nome,
    inscricaoEstadual: item.inscricaoEstadual,
    logradouro: item.endereco?.logradouro || '',
    numero: item.endereco?.numero || '',
    complemento: item.endereco?.complemento,
    bairro: item.endereco?.bairro || '',
    municipioId: item.endereco?.municipioId ?? 0,
    cep: item.endereco?.cep || '',
    email: item.email,
    telefone: item.telefone,
  };
};

// ---- Main modal ----

interface ModalImportarDestinatarioProps {
  show: boolean;
  onHide: () => void;
  onImportar: (dados: AtualizarDestinatarioRequest, municipioDisplay: string) => void;
}

const ModalImportarDestinatario: React.FC<ModalImportarDestinatarioProps> = ({
  show,
  onHide,
  onImportar,
}) => {
  const [termo, setTermo] = useState('');
  const [fonte, setFonte] = useState<Fonte>('pessoa');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debouncedTermo = useDebounce(termo);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (show) {
      setTermo('');
      setFonte('pessoa');
      setFocusedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [show]);

  const query = useQuery({
    queryKey: ['nfe-buscar-destinatario', fonte, debouncedTermo],
    queryFn: () => buscarDestinatarios(debouncedTermo, fonte),
    staleTime: 30_000,
    enabled: show,
  });

  const items = useMemo(() => query.data ?? [], [query.data]);

  useEffect(() => {
    setFocusedIndex(items.length > 0 ? 0 : -1);
  }, [items.length]);

  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[focusedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const handleSelect = useCallback((item: DestinatarioItem) => {
    const display = item.endereco?.municipio && item.endereco?.uf
      ? `${item.endereco.municipio} - ${item.endereco.uf}`
      : '';
    onImportar(itemToRequest(item), display);
    onHide();
  }, [onImportar, onHide]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(i => Math.min(i + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && items[focusedIndex]) handleSelect(items[focusedIndex]);
        break;
      case 'Escape':
        onHide();
        break;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      dialogClassName="cmd-modal-dialog"
      backdropClassName="cmd-backdrop"
      contentClassName="cmd-dialog border-0"
    >
      {/* Search row */}
      <div className="cmd-search-row">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="cmd-search-icon" />
        <input
          ref={inputRef}
          className="cmd-input"
          type="text"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar por nome, CNPJ, CPF…"
          autoComplete="off"
          spellCheck={false}
        />
        <button className="cmd-close" onClick={onHide} type="button">
          esc
        </button>
      </div>

      {/* Fonte toggle */}
      <div className="cmd-toggle">
        <button
          type="button"
          className={`cmd-pill${fonte === 'pessoa' ? ' cmd-pill--active' : ''}`}
          onClick={() => setFonte('pessoa')}
        >
          Pessoas
        </button>
        <button
          type="button"
          className={`cmd-pill${fonte === 'empresa' ? ' cmd-pill--active' : ''}`}
          onClick={() => setFonte('empresa')}
        >
          Empresas
        </button>
      </div>

      {/* Results */}
      <div className="cmd-results" ref={listRef}>
        {query.isLoading && (
          <div className="cmd-spinner">
            <Spinner size="sm" animation="border" variant="secondary" />
          </div>
        )}

        {query.isSuccess && items.length === 0 && (
          <div className="cmd-empty">Nenhum resultado</div>
        )}

        {items.map((item, i) => (
          <div
            key={item.id}
            className={`cmd-row${focusedIndex === i ? ' cmd-row--focused' : ''}`}
            role="button"
            tabIndex={-1}
            onClick={() => handleSelect(item)}
            onMouseEnter={() => setFocusedIndex(i)}
          >
            <span className={`cmd-badge cmd-badge--${item.tipoPessoa === 'J' ? 'pj' : 'pf'}`}>
              {item.tipoPessoa === 'J' ? 'PJ' : 'PF'}
            </span>
            <span className="cmd-row__name">{item.nome}</span>
            {item.documento && (
              <span className="cmd-row__doc">{item.documento}</span>
            )}
          </div>
        ))}
      </div>

      {/* Footer hints */}
      <div className="cmd-footer">
        <span className="cmd-hint"><kbd className="cmd-key">↑↓</kbd> navegar</span>
        <span className="cmd-hint"><kbd className="cmd-key">↵</kbd> selecionar</span>
        <span className="cmd-hint"><kbd className="cmd-key">esc</kbd> fechar</span>
      </div>
    </Modal>
  );
};

export default ModalImportarDestinatario;
